import { BaseUISchema } from '../common-types'
import { JsonFormSchemaV2 } from '../FormComponent/types'
import { ChartConfig } from '@/components/shadcn/charts'

export interface ChartScatterResponse {
  uiSchema: ChartScatterUISchema
  dataSchema: ChartScatterDataSchema
  filters: JsonFormSchemaV2 | Record<string, never>
}

interface BaseAxisConfig {
  dataKey: string
  unit?: string
  name?: string
}

export interface XYAxisConfig extends BaseAxisConfig {
  label?: string
  type?: 'number' | 'category'
  /** Explicit category/number domain order. Required for reliable category axis ordering. */
  domain?: Array<string | number>
}

export interface ZAxisConfig extends BaseAxisConfig {
  range?: number[]
  /** When true, render a size legend derived from point z-values. */
  showSizeLegend?: boolean
}

export interface TooltipConfig {
  showX?: boolean // Whether to show X value in tooltip (default: true)
  showY?: boolean // Whether to show Y value in tooltip (default: true)
  showZ?: boolean // Whether to show Z value in tooltip (default: true)
  fields?: TooltipField[] // Additional fields to show in tooltip
}

export type ChartScatterRenderMode = 'multiSeries' | 'singleSeries'

export interface ChartScatterChartConfig {
  dotStyle?: 'bubble' | 'solid'
  /**
   * `multiSeries` (default): colour by series key, data is SeriesData[].
   * `singleSeries`: one point list coloured by `colorKey`, data is DataPoint[].
   */
  renderMode?: ChartScatterRenderMode
  /** Data field used for per-point colour when renderMode is `singleSeries`. */
  colorKey?: string
  xAxis: XYAxisConfig
  yAxis: XYAxisConfig
  zAxis: ZAxisConfig
  config: ChartConfig
  tooltip?: TooltipConfig // Optional tooltip configuration
}

export interface ChartScatterUISchema extends BaseUISchema {
  chartConfig: ChartScatterChartConfig
}

/** A single scatter/bubble point. Axis keys are configured via xAxis/yAxis/zAxis.dataKey. */
export type DataPoint = Record<string, number | string>

export interface TooltipField {
  dataKey: string
  label: string
  unit?: string // Optional unit suffix for the field
}

export interface SeriesData {
  [key: string]: DataPoint[] | undefined
}

export interface ChartScatterDataSchema {
  /**
   * `multiSeries`: array of single-key objects `{ [seriesName]: DataPoint[] }`.
   * `singleSeries`: flat array of DataPoint objects coloured by `colorKey`.
   */
  data: SeriesData[] | DataPoint[]
}

export function isSingleSeriesData(data: SeriesData[] | DataPoint[]): data is DataPoint[] {
  if (data.length === 0) return true
  const first = data[0]
  if (typeof first !== 'object' || first === null || Array.isArray(first)) return false
  const values = Object.values(first)
  // SeriesData entry looks like { seriesName: DataPoint[] }; DataPoint values are scalars.
  return !values.some((value) => Array.isArray(value))
}
